/**
 * Commodity utility functions for legend and color management
 * Provides classification, color mapping, and data extraction for commodities
 */

// Import config for commodity classification and colors
import { commodityConfig } from '../config/commodityConfig.js';

/**
 * Extract unique commodities from mine data array
 * @param {Array} mineData - Array of mine objects
 * @returns {Array} Array of unique commodity strings
 */
export function extractCommoditiesFromMines(mineData) {
  if (!Array.isArray(mineData) || mineData.length === 0) {
    return [];
  }

  const commoditySet = new Set();

  mineData.forEach(mine => {
    getMineCommodityNames(mine).forEach((name) => {
      commoditySet.add(name.toLowerCase());
    });
  });

  return Array.from(commoditySet).sort();
}

/**
 * Returns the full list of commodity names associated with a mine, including
 * junction (secondary/tertiary) commodities. Prefers the flattened
 * `all_commodities_csv` string that minesToGeoJSON writes for each feature so
 * the legend counts mines by every commodity they produce, not just the
 * primary. Falls back to legacy shapes for safety.
 * @param {Object} mine - Mine object (typically feature.properties)
 * @returns {string[]} Array of trimmed commodity names (may be empty)
 */
export function getMineCommodityNames(mine) {
  if (!mine) return [];
  const out = [];
  const seen = new Set();
  const push = (raw) => {
    if (typeof raw !== 'string') return;
    raw.split(/[,;|]/).forEach((part) => {
      const trimmed = part.trim();
      if (!trimmed) return;
      const key = trimmed.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      out.push(trimmed);
    });
  };

  if (typeof mine.all_commodities_csv === 'string') {
    push(mine.all_commodities_csv);
  }

  if (mine.commodity && typeof mine.commodity === 'object' && mine.commodity.commodity_name) {
    push(mine.commodity.commodity_name);
  } else if (typeof mine.commodity === 'string') {
    push(mine.commodity);
  }

  const fallback = mine.commodities || mine.mineral || mine.minerals;
  if (Array.isArray(fallback)) {
    fallback.forEach((c) => typeof c === 'string' && push(c));
  } else if (typeof fallback === 'string') {
    push(fallback);
  }

  return out;
}

/**
 * Get color for a specific commodity
 * @param {string} commodity - Commodity name (case insensitive)
 * @returns {string} Hex color code
 */
export function getCommodityColor(commodity) {
  if (!commodity || typeof commodity !== 'string') {
    return commodityConfig?.DEFAULT_COMMODITY_COLOR || '#6B7280';
  }

  const normalizedCommodity = commodity.toLowerCase().trim();

  // First check specific commodity colors
  const specificColor = commodityConfig?.COMMODITY_COLORS?.[normalizedCommodity];
  if (specificColor) {
    return specificColor;
  }

  // Check uncommon material colors
  const uncommonColor = commodityConfig?.UNCOMMON_MATERIAL_COLORS?.[normalizedCommodity];
  if (uncommonColor) {
    return uncommonColor;
  }

  // Check if it's a critical mineral
  const isCritical = isCriticalMineral(commodity);
  if (isCritical) {
    return commodityConfig?.CRITICAL_COMMODITY_COLOR || '#FF6B6B';
  }

  // Check if it's non-critical
  const isNonCritical = isNonCriticalMineral(commodity);
  if (isNonCritical) {
    return commodityConfig?.NON_CRITICAL_COMMODITY_COLOR || '#95A5A6';
  }

  // Default color for unknown commodities
  return commodityConfig?.DEFAULT_COMMODITY_COLOR || '#6B7280';
}

/**
 * Check if a commodity is classified as critical
 * @param {string} commodity - Commodity name
 * @returns {boolean} True if critical mineral
 */
export function isCriticalMineral(commodity) {
  if (!commodity || typeof commodity !== 'string') {
    return false;
  }

  const normalizedCommodity = commodity.toLowerCase().trim();
  const criticalMinerals = commodityConfig?.CRITICAL_MINERALS || [];

  return criticalMinerals.some(critical =>
    critical.toLowerCase() === normalizedCommodity
  );
}

/**
 * Check if a commodity is classified as non-critical
 * @param {string} commodity - Commodity name
 * @returns {boolean} True if non-critical mineral
 */
export function isNonCriticalMineral(commodity) {
  if (!commodity || typeof commodity !== 'string') {
    return false;
  }

  const normalizedCommodity = commodity.toLowerCase().trim();
  const nonCriticalMinerals = commodityConfig?.NON_CRITICAL_MINERALS || [];

  return nonCriticalMinerals.some(nonCritical =>
    nonCritical.toLowerCase() === normalizedCommodity
  );
}

/**
 * Get display name for commodity (capitalize first letter of each word)
 * @param {string} commodity - Raw commodity name
 * @returns {string} Formatted display name
 */
export function getCommodityDisplayName(commodity) {
  if (!commodity || typeof commodity !== 'string') {
    return 'Unknown';
  }

  return commodity
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Count mines for a specific commodity in mine data
 * @param {Array} mineData - Array of mine objects
 * @param {string} commodity - Commodity to count
 * @returns {number} Number of mines with this commodity
 */
export function countMinesForCommodity(mineData, commodity) {
  if (!Array.isArray(mineData) || !commodity) {
    return 0;
  }

  const normalizedTarget = commodity.toLowerCase().trim();

  return mineData.filter((mine) => {
    const names = getMineCommodityNames(mine);
    return names.some((n) => n.toLowerCase() === normalizedTarget);
  }).length;
}

/**
 * Build legend data structure from mine data
 * @param {Array} mineData - Array of mine objects
 * @returns {Object} Legend data with commodities array and metadata
 */
export function buildLegendData(mineData) {
  const uniqueCommodities = extractCommoditiesFromMines(mineData);

  const legendCommodities = uniqueCommodities.map(commodity => ({
    name: commodity,
    displayName: getCommodityDisplayName(commodity),
    color: getCommodityColor(commodity),
    isCritical: isCriticalMineral(commodity),
    count: countMinesForCommodity(mineData, commodity)
  }));

  // Sort alphabetically by display name
  legendCommodities.sort((a, b) => {
    return a.displayName.localeCompare(b.displayName);
  });

  return {
    visibleCommodities: legendCommodities,
    lastUpdated: Date.now(),
    isLoading: false,
    totalCommodities: legendCommodities.length,
    criticalCount: legendCommodities.filter(c => c.isCritical).length
  };
}

/**
 * Validate commodity name format
 * @param {string} commodity - Commodity name to validate
 * @returns {boolean} True if valid format
 */
export function isValidCommodityName(commodity) {
  if (!commodity || typeof commodity !== 'string') {
    return false;
  }

  const trimmed = commodity.trim();
  return trimmed.length > 0 && trimmed.length <= 100;
}